import { useCallback, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const VAPID_PUBLIC_KEY = 'BOEmtFfrdJH11k8kP4DQPaTwzQm3fxQCZUSpJHPH5bh3KjRw62NsFl2fOrIGOEgZ5MMuANyFFE7io-uG2sVoVqk';

// =====================================================================
// iSentiAlert (D16) → SUPERSEDED by v0.4.1+ architectural pivot
//
// The original iSentiAlert APK was a SEPARATE companion app to the PWA.
// It connected to the iSentiAlert WebSocket and posted native Android
// notifications. To avoid double-alerting the user (PWA web-push +
// iSentiAlert native), the Settings page had a "Browser notifications"
// toggle with an "auto" mode that followed the iSentiAlert pair state:
// when iSentiAlert was paired, web-push was disabled (because the
// native notifications covered it).
//
// As of v0.4.1, the iSentiAlert APK has been replaced by iSentinel360 —
// a single APK that IS the PWA (a WebView wrapper at
// https://app.isentinel360.com). There is no longer a separate app to
// pair, so the "double alert" risk is gone. Web-push via the WebView
// is now the PRIMARY (and only) channel for security alerts.
//
// This file (PushHandler.tsx) and Settings.tsx have been updated to
// reflect this:
//   - The `effectiveWebPushEnabled()` function and the `isentialertLinked`
//     check are GONE. The PWA always subscribes to web-push.
//   - The Settings page no longer shows the "Browser notifications"
//     toggle.
//   - The `isentialertLinked` localStorage key may still be written by
//     AuthContext's legacy pair-claim piggyback, but no one reads it
//     for notification routing anymore. (Pair-claim cleanup is a
//     separate task.)
// =====================================================================

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushHandler() {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const isSubscribed = useRef(false);
  // v0.4.9.2.4c4: guard against overlapping silent refreshes. See the
  // note inside the refresh effect.
  const refreshInFlight = useRef(false);

  async function setupPush() {
    // v0.4.1+: web-push is ALWAYS enabled when the user is authenticated.
    // The previous "effectiveWebPushEnabled()" check (which disabled
    // web-push when iSentiAlert was paired) has been removed. The APK
    // IS the PWA now, so there's no separate iSentiAlert app to avoid
    // double-alerting.
    //
    // We still respect the browser's Notification.permission state.
    // If the user has denied notifications at the OS level, we don't
    // subscribe. If permission is 'default', we request it.

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[PushHandler] Push not supported in this browser');
      return;
    }
    if (Notification.permission === 'denied') {
      console.log('[PushHandler] Notification permission denied by user; skipping web-push setup');
      isSubscribed.current = false;
      return;
    }
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('[PushHandler] Notification permission not granted; skipping web-push setup');
        return;
      }
    }
    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      // If subscription exists but is invalid (e.g., expired), unsubscribe
      if (subscription && !subscription.endpoint) {
        await subscription.unsubscribe();
        subscription = null;
      }
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
      if (subscription) {
        await api.post('/api/push/subscribe', { subscription: subscription.toJSON() });
        console.log('[PushHandler] Web-push subscription saved/updated');
        isSubscribed.current = true;
      }
    } catch (err) {
      console.error('[PushHandler] Push setup error:', err);
      isSubscribed.current = false;
    }
  }

  // v0.4.9.2.4c1 — Silent JWT refresh. Shared by the timer effect AND the
  // mount bootstrap above, so there is exactly one implementation.
  //
  // Returns true when a fresh token was obtained. This doubles as the token
  // validation: POST /api/auth/refresh verifies the JWT, re-signs it, and
  // bumps last_active_at, so a successful return means the server accepted
  // the session.
  //
  // CRITICAL — locked rule (see handover §05 / §09):
  //   NEVER `await fetch(...)` between "read stored auth" and "re-arm the
  //   bridge". The bridge payload uses `res.token` (NOT a re-read of
  //   localStorage) so a concurrent tab cannot make the APK carry a stale JWT.
  const refreshNow = useCallback(async (): Promise<boolean> => {
    // v0.4.9.2.4c4: never run two refreshes concurrently. The server rotates
    // the session row onto the new token, so a second overlapping refresh
    // would present the now-stale hash, fail the rotation, get a 401, and
    // kick the user.
    if (refreshInFlight.current) return false;
    refreshInFlight.current = true;
    try {
      const res = await api.refreshToken();
      localStorage.setItem('authToken', res.token);
      if ((window as any).Android?.setAuth) {
        const storeId = localStorage.getItem('activeStoreId') || '';
        (window as any).Android.setAuth(JSON.stringify({
          jwt: res.token,
          userId: res.user?.id,
          storeId,
          displayName: res.user?.name || res.user?.email,
        }));
      }
      return true;
    } catch {
      // 401 handled by authFetch (kick flow). 5xx swallowed silently.
      return false;
    } finally {
      refreshInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    // v0.4.9.4.x: on mount, refresh FIRST, subscribe SECOND.
    //
    // `isAuthenticated` is `!!user`, and `user` is restored from localStorage
    // WITHOUT the token being validated (AuthContext c17 dropped the /me
    // pre-flight). So on every page load both this effect and the silent-
    // refresh effect fired simultaneously with whatever token localStorage
    // happened to hold — which is the PREVIOUS session's token whenever the
    // app was reopened. setupPush then POSTed /api/push/subscribe with it and
    // the server answered 401.
    //
    // The fix needs no new endpoint: POST /api/auth/refresh already verifies
    // the JWT, re-signs it, and bumps last_active_at. Running it first makes
    // the subscribe call carry a token the server has just accepted.
    //
    // Still fire-and-forget: the page stays usable while this runs, exactly
    // as designed. A failed refresh means the session really is dead —
    // authFetch has already kicked — and there is nothing to subscribe with,
    // so we bail rather than issuing a second doomed request.
    let cancelled = false;
    void (async () => {
      const ok = await refreshNow();
      if (!cancelled && ok) await setupPush();
    })();

    // Re-subscribe every 24 hours to keep it alive
    const interval = setInterval(setupPush, 24 * 60 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAuthenticated]);

  // ---------------------------------------------------------------
  // v0.4.9.2.4c1 — Silent JWT refresh (12h interval).
  //
  // Active users should never be logged out. Every 12 hours the
  // client silently re-signs the JWT while the current one is still
  // valid. The new token is written to localStorage AND pushed to the
  // Android bridge.
  //
  // CRITICAL — locked rule (see handover §05 / §09):
  //   NEVER `await fetch(...)` between "read stored auth" and
  //   "re-arm the bridge". Re-arm immediately (sync, fire-and-
  //   forget). This handler reads `activeStoreId` AFTER the await
  //   for the refresh response, but uses `res.token` (NOT a re-read
  //   of localStorage) for the bridge payload — so even if a
  //   concurrent tab logs in and overwrites localStorage mid-call,
  //   the Android bridge gets the freshly-issued token.
  //
  // On 401 the authFetch wrapper triggers the kick flow; we swallow
  // the throw so the interval can continue trying on the next tick
  // (no point spamming logs).
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated) return;

    const id = setInterval(refreshNow, 12 * 60 * 60 * 1000);

    // ---------------------------------------------------------------
    // v0.4.9.2.3c3: refresh on wake, not just on the timer.
    //
    // A wall-mounted monitoring tablet or a guard phone that is asleep
    // for 20 hours does not run its 12h interval. It would sit on a
    // stale token until the timer eventually fires. Two triggers close that:
    //   1. once on mount (covers "reopened the app")
    //   2. on every visibilitychange → visible (covers "woke from sleep")
    //
    // The MOUNT trigger now lives in the push effect above, which awaits it
    // before subscribing. It is deliberately not repeated here: two mount
    // refreshes would race on `refreshInFlight`, and the loser gets `false`,
    // which would skip /api/push/subscribe entirely on that page load.
    //
    // Both remaining triggers are fire-and-forget: the page is already usable
    // when they run, and a failure just means the next trigger tries again.
    // ---------------------------------------------------------------
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshNow();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [isAuthenticated]);

  // v0.4.1+: removed the cross-tab storage listener. Previously, it
  // re-ran setupPush when `isentialertLinked` or `browserNotificationsOverride`
  // changed in another tab. Neither key drives web-push routing anymore.
  // The local-storage event listener is kept here as a no-op so other
  // code (e.g., AuthContext's pair-claim) can keep using the storage
  // event without this component doing anything.

  return null;
}
